import { useState, useEffect, useRef, useMemo } from 'react';
import type { LeadPipelineStage } from '@evo-hub/shared';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/Icon';
import { EmptyState } from '../components/ui/EmptyState.js';
import { LoadingState } from '../components/ui/LoadingState.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Input } from '../components/ui/Input.js';
import { Textarea } from '../components/ui/Textarea.js';
import { Chip } from '../components/ui/Chip.js';
import { Pressable } from '../components/ui/Pressable.js';
import { SelectableCard } from '../components/ui/SelectableCard.js';
import { Checkbox } from '../components/ui/Checkbox.js';
import { FilterRow } from '../components/ui/FilterRow.js';
import { Dropdown } from '../components/ui/Dropdown.js';
import { SelectionActionBar } from '../components/ui/SelectionActionBar.js';
import { SortableColumnHeader } from '../components/ui/SortableColumnHeader.js';
import { PillGroup, Pill } from '../components/PillGroup.js';
import { useTopBarCtx } from '../contexts/TopBarContext.js';
import { parseServerDate } from '../lib/date.js';
import {
  useLeads,
  useInfiniteLeads,
  useFilteredLeadIds,
  usePipelineCounts,
  useBulkUpdateStage,
  useBulkDeleteLeads,
  useBulkAnalyze,
  useBulkDraft,
  useBulkSend,
  useBulkJunk,
  useBulkArchive,
  useBulkReactivate,
  useBulkConvertLeads,
  useCreateLead,
  useDueDraftsCount,
  useRequalifyLead,
  type LeadRow,
  type LeadFilters,
} from '../hooks/useLeads.js';
import { TimeBandedList } from '../components/common/TimeBandedList.js';
import { useToast } from '../components/ui/Toast.js';
import {
  useLeadPipelines,
  useRunPipelineNow,
  useCreateLeadPipeline,
  type LeadPipelineInput,
} from '../hooks/useSettings.js';
import { PipelineEditorModal } from '../components/leads/PipelineEditorModal.js';
import { useListKeyboardNav } from '../hooks/useListKeyboardNav.js';
import { getLeadStatusMeta, getDisplayStage, getTerminalReason } from '../lib/leadStatusLabel.js';

function filtersFromParams(params: URLSearchParams): LeadFilters {
  const f: LeadFilters = { page: 1, limit: 50 };
  const search = params.get('search'); if (search) f.search = search;
  const pipelineStage = params.get('pipelineStage'); if (pipelineStage) f.pipelineStage = pipelineStage;
  const city = params.get('city'); if (city) f.city = city;
  const hasWebsite = params.get('hasWebsite'); if (hasWebsite) f.hasWebsite = hasWebsite === 'true';
  const hasEmail = params.get('hasEmail'); if (hasEmail) f.hasEmail = hasEmail === 'true';
  if (params.get('hasDueDrafts') === 'true') f.hasDueDrafts = true;
  const page = params.get('page'); if (page) f.page = parseInt(page);
  const sortBy = params.get('sortBy');
  if (sortBy === 'businessName' || sortBy === 'city' || sortBy === 'status' || sortBy === 'updatedAt') f.sortBy = sortBy;
  const sortDir = params.get('sortDir');
  if (sortDir === 'asc' || sortDir === 'desc') f.sortDir = sortDir;
  return f;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const STAGES: Array<{ id: LeadPipelineStage; color: string }> = [
  { id: 'new', color: '#6b7280' },
  { id: 'analyzed', color: '#3b82f6' },
  { id: 'drafted', color: 'var(--evo-amber)' },
  { id: 'sent', color: 'var(--evo-cyan-dark, #0891b2)' },
  { id: 'replied', color: '#f97316' },
  { id: 'closed_won', color: 'var(--evo-cyan)' },
  { id: 'closed_lost', color: 'var(--evo-danger)' },
  { id: 'no_contact', color: 'var(--evo-fg-3)' },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmtDate(s: string | null): string {
  if (!s) return '—';
  return parseServerDate(s).toLocaleDateString('es', { month: 'short', day: 'numeric', year: '2-digit' });
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function ReasonBadge({ lead }: { lead: LeadRow }) {
  const { t } = useTranslation();
  const reason = getTerminalReason(lead);
  if (!reason) return null;
  const label = reason.fallbackKey
    ? t(reason.labelKey, { defaultValue: t(reason.fallbackKey) })
    : t(reason.labelKey);
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', height: '18px', padding: '0 8px',
      borderRadius: 9999, fontSize: '10px', fontWeight: 500, fontFamily: 'var(--evo-font)',
      border: `1px solid var(${reason.colorVar})`,
      color: `var(${reason.colorVar})`,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}


function StageBadge({ stage, junkReason }: { stage: string; junkReason?: string | null }) {
  const { t } = useTranslation();
  const { tone, labelKey } = getLeadStatusMeta(stage);
  const toneColorMap: Record<string, string> = {
    neutral: 'var(--evo-fg-3)',
    info: 'var(--evo-cyan)',
    success: 'var(--evo-green, #22c55e)',
    warn: 'var(--evo-amber)',
    danger: 'var(--evo-danger)',
    muted: 'var(--evo-fg-4)',
  };
  const color = toneColorMap[tone] ?? 'var(--evo-fg-3)';
  const label = (() => {
    if (stage === 'archived' && junkReason) {
      if (junkReason === 'no_email' || junkReason === 'invalid_format') return t('leads.statusLabels.disqualified');
      if (junkReason === 'auto_closed_no_response') return t('leads.statusLabels.archived');
    }
    return t(labelKey, { defaultValue: stage });
  })();
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        height: '18px',
        padding: '0 8px',
        fontSize: '10px',
        fontWeight: 500,
        fontFamily: 'var(--evo-font)',
        color,
        background: 'transparent',
        border: `1px solid ${color}`,
        borderRadius: 100,
        whiteSpace: 'nowrap',
        lineHeight: 1,
      }}
    >
      {label}
    </span>
  );
}

function ScoreBar({ value, max = 100 }: { value: number | null; max?: number }) {
  if (value === null) return <span style={{ color: 'var(--evo-fg-3)' }}>—</span>;
  const pct = Math.min(100, (value / max) * 100);
  const color = pct >= 70 ? 'var(--evo-cyan)' : pct >= 40 ? 'var(--evo-amber)' : 'var(--evo-danger)';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full" style={{ background: 'var(--evo-bg-2)' }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: color }} />
      </div>
      <span className="text-xs w-8 text-right" style={{ color }}>{value}</span>
    </div>
  );
}

function PriorityBadge({ score }: { score: number }) {
  const color = score >= 8 ? 'var(--evo-cyan)' : score >= 5 ? 'var(--evo-amber)' : 'var(--evo-fg-3)';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '18px',
        height: '18px',
        borderRadius: '3px',
        fontSize: '10px',
        fontWeight: 600,
        fontFamily: 'var(--evo-font)',
        color,
        border: `1px solid ${color}`,
        background: 'transparent',
        flexShrink: 0,
      }}
    >
      {score}
    </span>
  );
}


// ── New Lead Modal ────────────────────────────────────────────────────────────

function NewLeadModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const { t } = useTranslation();
  const createLead = useCreateLead();
  const [form, setForm] = useState({
    businessName: '', contactEmail: '', phone: '', websiteUrl: '',
    city: '', category: '', address: '', notes: '',
  });

  function setField(key: keyof typeof form, val: string) {
    setForm(f => ({ ...f, [key]: val }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.businessName.trim()) return;
    const body = Object.fromEntries(
      Object.entries(form).filter(([, v]) => v.trim() !== ''),
    ) as typeof form;
    createLead.mutate(body, {
      onSuccess: lead => { onCreated(lead.id); onClose(); },
    });
  }

  const fields: Array<{ key: keyof typeof form; label: string; placeholder: string; required?: boolean; type?: string }> = [
    { key: 'businessName', label: t('leads.add.businessNameLabel'), placeholder: t('leads.add.businessNamePlaceholder'), required: true },
    { key: 'contactEmail', label: t('leads.add.contactEmailLabel'), placeholder: t('leads.add.contactEmailPlaceholder'), type: 'email' },
    { key: 'phone', label: t('leads.add.phoneLabel'), placeholder: t('leads.add.phonePlaceholder'), type: 'tel' },
    { key: 'websiteUrl', label: t('leads.add.websiteLabel'), placeholder: t('leads.add.websitePlaceholder'), type: 'url' },
    { key: 'city', label: t('leads.add.cityLabel'), placeholder: t('leads.add.cityPlaceholder') },
    { key: 'category', label: t('leads.add.categoryLabel'), placeholder: t('leads.add.categoryPlaceholder') },
    { key: 'address', label: t('leads.add.addressLabel'), placeholder: t('leads.add.addressPlaceholder') },
  ];

  return (
    <>
      <div className="fixed inset-0 z-30" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <div
        className="fixed top-1/2 left-1/2 z-40 -translate-x-1/2 -translate-y-1/2 w-[440px] p-6 rounded-lg"
        style={{ background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)' }}
      >
        <h3 className="font-semibold mb-4" style={{ fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)' }}>
          {t('leads.add.title')}
        </h3>
        <form onSubmit={handleSubmit} className="space-y-2.5">
          {fields.map(f => (
            <div key={f.key} className="flex items-center gap-3">
              <label className="text-xs shrink-0" style={{ color: 'var(--evo-fg-3)', width: '130px' }}>{f.label}</label>
              <Input
                type={(f.type ?? 'text') as 'text' | 'email' | 'tel' | 'url'}
                size="sm"
                fullWidth
                value={form[f.key]}
                onChange={v => setField(f.key, v)}
                placeholder={f.placeholder}
                required={f.required ?? false}
              />
            </div>
          ))}
          <div className="flex items-start gap-3">
            <label className="text-xs shrink-0 pt-1.5" style={{ color: 'var(--evo-fg-3)', width: '130px' }}>{t('leads.add.notesLabel')}</label>
            <Textarea
              value={form.notes}
              onChange={v => setField('notes', v)}
              placeholder={t('leads.add.notesPlaceholder')}
              rows={2}
              fullWidth
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              type="submit"
              variant="solid"
              tone="cyan"
              size="sm"
              fullWidth
              loading={createLead.isPending}
              disabled={createLead.isPending || !form.businessName.trim()}
            >
              {t('leads.add.submitBtn')}
            </Button>
            <Button
              variant="ghost"
              tone="muted"
              size="sm"
              onClick={onClose}
            >
              {t('leads.add.cancelBtn')}
            </Button>
          </div>
        </form>
      </div>
    </>
  );
}

// ── Run Pipeline Modal ────────────────────────────────────────────────────────

function RunPipelineModal({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const { data: pipelines = [], isLoading } = useLeadPipelines();
  const runNow = useRunPipelineNow();
  const create = useCreateLeadPipeline();
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [showEditor, setShowEditor] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);

  function handleRun() {
    if (!selectedId) return;
    setRunError(null);
    runNow.mutate(selectedId, {
      onSuccess: () => onClose(),
      onError: (err: Error) => setRunError(err.message),
    });
  }

  function handleCreateSave(data: LeadPipelineInput) {
    create.mutate(data, {
      onSuccess: result => {
        setSelectedId(result.id);
        setShowEditor(false);
      },
    });
  }

  return (
    <>
      <div className="fixed inset-0 z-30" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} />
      <div
        className="fixed top-1/2 left-1/2 z-40 -translate-x-1/2 -translate-y-1/2 w-[480px] max-h-[80vh] flex flex-col rounded-lg"
        style={{ background: 'var(--evo-bg-3)', border: '1px solid var(--evo-stroke)' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-5 py-4 shrink-0"
          style={{ borderBottom: '1px solid var(--evo-stroke)' }}
        >
          <h3 className="font-semibold text-sm" style={{ fontFamily: 'var(--evo-font)', color: 'var(--evo-cyan)' }}>
            {t('leads.runPipelineModal.title')}
          </h3>
          <IconButton variant="ghost" tone="muted" size="sm" ariaLabel="Close" icon={<Icon name="close" size={16} />} onClick={onClose} />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <p className="text-sm" style={{ color: 'var(--evo-fg-3)' }}>{t('common.loading')}</p>
          ) : pipelines.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: 'var(--evo-fg-3)' }}>
              {t('leads.runPipelineModal.noPipelines')}
            </p>
          ) : (
            <div className="space-y-2" role="radiogroup">
              <p className="text-xs mb-3" style={{ color: 'var(--evo-fg-3)' }}>
                {t('leads.runPipelineModal.selectHint')}
              </p>
              {pipelines.map(p => (
                <SelectableCard
                  key={p.id}
                  role="radio"
                  selected={selectedId === p.id}
                  onSelect={() => setSelectedId(p.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--evo-space-3)', width: '100%' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p className="text-sm font-medium truncate" style={{ color: 'var(--evo-fg-1)', fontFamily: 'var(--evo-font)' }}>
                        {p.name || <span style={{ color: 'var(--evo-fg-4)', fontStyle: 'italic' }}>{t('settings.pipelines.namePlaceholder')}</span>}
                      </p>
                      <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--evo-fg-3)' }}>
                        {[
                          p.categories.slice(0, 3).join(', ') + (p.categories.length > 3 ? ` +${p.categories.length - 3}` : ''),
                          p.zones.length > 0 ? p.zones.slice(0, 2).map(z => z.city).join(', ') + (p.zones.length > 2 ? ` +${p.zones.length - 2}` : '') : '',
                        ].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    {p.enabled === 0 && (
                      <span className="text-xs shrink-0" style={{ color: 'var(--evo-fg-4)' }}>
                        {t('leads.runPipelineModal.disabled')}
                      </span>
                    )}
                  </div>
                </SelectableCard>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 shrink-0" style={{ borderTop: '1px solid var(--evo-stroke)' }}>
          {runError && (
            <p className="text-xs mb-2" style={{ color: 'var(--evo-danger)' }}>{runError}</p>
          )}
          <div className="flex items-center gap-2">
            <Button variant="outline" tone="muted" size="sm" onClick={() => setShowEditor(true)}>
              + {t('leads.runPipelineModal.createNew')}
            </Button>
            <div className="flex-1" />
            <Button variant="outline" tone="muted" size="sm" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="solid"
              tone="cyan"
              size="sm"
              loading={runNow.isPending}
              disabled={!selectedId || runNow.isPending}
              onClick={handleRun}
            >
              {t('leads.runPipelineModal.runBtn')}
            </Button>
          </div>
        </div>
      </div>

      {showEditor && (
        <PipelineEditorModal
          onSave={handleCreateSave}
          onCancel={() => setShowEditor(false)}
          saving={create.isPending}
          error={create.isError ? (create.error).message : null}
        />
      )}
    </>
  );
}

// ── Pipeline Board View ────────────────────────────────────────────────────────

function PipelineBoard({ onSelect }: { onSelect: (lead: LeadRow) => void }) {
  const { t } = useTranslation();
  const counts = usePipelineCounts();

  const stagesWithCounts = STAGES.map(s => ({
    ...s,
    count: counts.data?.find(c => c.stage === s.id)?.count ?? 0,
  }));

  // Fetch leads per visible stage
  const { data: allLeads } = useLeads({ limit: 200 });

  const grouped = new Map<string, LeadRow[]>();
  for (const stage of STAGES) grouped.set(stage.id, []);
  for (const lead of allLeads?.rows ?? []) {
    grouped.get(lead.pipelineStage)?.push(lead);
  }

  return (
    <div className="flex gap-3 h-full overflow-x-auto pb-2">
      {stagesWithCounts.map(stage => (
        <div
          key={stage.id}
          className="flex flex-col shrink-0 rounded-lg overflow-hidden"
          style={{
            width: '220px',
            background: 'var(--evo-bg-3)',
            border: '1px solid var(--evo-stroke)',
          }}
        >
          {/* Column header */}
          <div
            className="flex items-center justify-between px-3 py-2.5 shrink-0"
            style={{ borderBottom: `2px solid ${stage.color}` }}
          >
            <span className="text-xs font-semibold" style={{ color: stage.color, fontFamily: 'var(--evo-font)' }}>
              {t('leads.stages.' + stage.id)}
            </span>
            <span
              className="text-xs px-1.5 py-0.5 rounded-full"
              style={{ background: stage.color + '22', color: stage.color }}
            >
              {stage.count}
            </span>
          </div>

          {/* Cards */}
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            {(grouped.get(stage.id) ?? []).map(lead => (
              <Pressable key={lead.id} onPress={() => onSelect(lead)} fullWidth>
                <div className="p-2.5 rounded-md" style={{ border: '1px solid var(--evo-stroke)' }}>
                  <div className="flex items-start gap-1.5 mb-1">
                    {lead.priorityScore !== null && lead.analysisStatus === 'completed' && (
                      <PriorityBadge score={lead.priorityScore} />
                    )}
                    <p className="text-xs font-medium leading-snug flex-1 min-w-0 truncate">
                      {lead.businessName}
                    </p>
                  </div>
                  <p className="text-xs" style={{ color: 'var(--evo-fg-3)' }}>
                    {lead.city ?? '—'}
                  </p>
                  {lead.auditScorePagespeedMobile !== null && (
                    <div className="mt-1.5">
                      <ScoreBar value={lead.auditScorePagespeedMobile} />
                    </div>
                  )}
                </div>
              </Pressable>
            ))}
            {(grouped.get(stage.id) ?? []).length === 0 && (
              <EmptyState size="sm" reserveSpace title={t('leads.emptyStage')} />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── List View ─────────────────────────────────────────────────────────────────

function LeadsList({
  filters,
  selected,
  onSelect,
  onToggleSelect,
  onFiltersChange,
}: {
  filters: LeadFilters;
  selected: Set<string>;
  onSelect: (lead: LeadRow) => void;
  onToggleSelect: (id: string) => void;
  onFiltersChange?: (f: LeadFilters) => void;
}) {
  const { t } = useTranslation();
  const { data: infiniteData, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteLeads(filters);
  const rows = useMemo(() => infiniteData?.pages.flatMap(p => p.rows) ?? [], [infiniteData]);
  const isTerminal = filters.pipelineStage === 'archived';
  const requalify = useRequalifyLead();

  function handleSort(key: 'businessName' | 'city' | 'status') {
    if (!onFiltersChange) return;
    const nextDir: 'asc' | 'desc' =
      filters.sortBy === key && filters.sortDir === 'asc' ? 'desc' : 'asc';
    onFiltersChange({ ...filters, page: 1, sortBy: key, sortDir: nextDir });
  }

  const sentinelRef = useRef<HTMLDivElement>(null);
  const [collapsedBands, setCollapsedBands] = useState<Set<string>>(new Set());

  useListKeyboardNav(rows, null, () => {});

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(entries => {
      if (entries[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage();
      }
    }, { threshold: 0.1 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div className="flex flex-col h-full">
      {/* Column headers */}
      {/* Tabular list layout rule: primary identity column (business name here)
          takes ~40% of flexible space; remaining content columns split the rest
          equally. When a second list view appears (contacts, emails), extract
          this grid + the SortableHeader pattern below into a shared primitive. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'calc(var(--evo-list-pad-x) + 16px + var(--evo-list-checkbox-gap)) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', height: 'var(--evo-toolbar-height)', alignItems: 'center', borderBottom: '1px solid var(--evo-stroke)', flexShrink: 0 }}>
        <div style={{ padding: '8px var(--evo-list-checkbox-gap) 8px var(--evo-list-pad-x)' }}>
          <Checkbox
            checked={rows.length > 0 && rows.every(r => selected.has(r.id))}
            indeterminate={selected.size > 0 && !rows.every(r => selected.has(r.id))}
            onChange={_ => {
              if (rows.every(r => selected.has(r.id))) {
                rows.forEach(r => onToggleSelect(r.id));
              } else {
                rows.filter(r => !selected.has(r.id)).forEach(r => onToggleSelect(r.id));
              }
            }}
          />
        </div>
        <SortableColumnHeader<'businessName' | 'city' | 'status'> label={t('leads.columns.business')} sortKey="businessName" currentSort={filters.sortBy} currentDir={filters.sortDir} onSort={handleSort} flush />
        <div style={{ padding: '8px 12px', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-header-fs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.88px' }}>
          {t('leads.columns.email')}
        </div>
        <div style={{ padding: '8px 12px', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-header-fs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.88px' }}>
          {t('leads.columns.web')}
        </div>
        <SortableColumnHeader<'businessName' | 'city' | 'status'> label={isTerminal ? t('leads.columns.reason') : t('leads.columns.status')} sortKey="status" currentSort={filters.sortBy} currentDir={filters.sortDir} onSort={handleSort} />
        <div style={{ padding: '8px 12px', color: 'var(--evo-fg-3)', fontSize: 'var(--evo-list-header-fs)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.88px', textAlign: 'right' }}>
          {isTerminal ? t('leads.columns.action') : t('leads.columns.updated')}
        </div>
      </div>

      {/* List with time bands */}
      <div className="flex-1 overflow-auto">
        {isLoading && <LoadingState size="sm" />}
        {!isLoading && rows.length === 0 && (
          <EmptyState size="md" reserveSpace title={t('leads.noResults')} />
        )}
        {rows.length > 0 && (
          <TimeBandedList<LeadRow, string>
            items={rows}
            getTimestamp={(l) => l.scrapedAt ?? l.updatedAt}
            getId={(l) => l.id}
            checkedIds={selected}
            onBandCheck={(ids, allChecked) => {
              ids.forEach(id => {
                const isSelected = selected.has(id);
                if (allChecked && isSelected) onToggleSelect(id);
                else if (!allChecked && !isSelected) onToggleSelect(id);
              });
            }}
            collapsedBands={collapsedBands}
            onToggleBand={(band) => setCollapsedBands(prev => {
              const next = new Set(prev);
              next.has(band) ? next.delete(band) : next.add(band);
              return next;
            })}
            renderItem={(lead) => (
              <div key={lead.id} style={{ display: 'flex', flexDirection: 'column' }}>
                <div
                  data-list-item-id={lead.id}
                  onClick={() => onSelect(lead)}
                  className="cursor-pointer transition-colors"
                  style={{ display: 'grid', gridTemplateColumns: 'calc(var(--evo-list-pad-x) + 16px + var(--evo-list-checkbox-gap)) minmax(0, 2fr) minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) minmax(0, 1fr)', alignItems: 'flex-start', minHeight: 'var(--evo-list-row-min-height)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = 'rgba(0,250,200,0.04)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = ''; }}
                >
                  <div style={{ padding: '14px var(--evo-list-checkbox-gap) 14px var(--evo-list-pad-x)' }} onClick={e => { e.stopPropagation(); onToggleSelect(lead.id); }}>
                    <div style={{ pointerEvents: 'none' }}>
                      <Checkbox checked={selected.has(lead.id)} onChange={() => {}} />
                    </div>
                  </div>
                  <div style={{ padding: '14px 12px 14px 0', overflow: 'hidden' }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="truncate" style={{ color: 'var(--evo-fg-1)', fontSize: 'var(--evo-list-title-fs)', fontWeight: 400 }}>{lead.businessName}</span>
                    </div>
                    <div className="truncate" style={{ color: 'var(--evo-fg-3)', marginTop: '2px', fontSize: 'var(--evo-list-subtitle-fs)' }}>{lead.city ?? ''}</div>
                  </div>
                  <div style={{ padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lead.contactEmail ?? lead.scrapedEmail ?? '—'}
                  </div>
                  <div style={{ padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lead.websiteUrl?.replace(/^https?:\/\//, '') ?? '—'}
                  </div>
                  {isTerminal ? (
                    <>
                      <div style={{ padding: '14px 12px', display: 'flex' }}>
                        <ReasonBadge lead={lead} />
                      </div>
                      <div style={{ padding: '14px 12px', display: 'flex' }} onClick={e => e.stopPropagation()}>
                        {lead.status === 'disqualified' && (
                          <Button
                            variant="outline" tone="cyan" size="sm"
                            disabled={requalify.isPending}
                            onClick={e => { e.stopPropagation(); requalify.mutate(lead.id); }}
                          >
                            {requalify.isPending && requalify.variables === lead.id ? t('leads.requalifyingBtn') : t('leads.requalifyBtn')}
                          </Button>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ padding: '14px 12px', display: 'flex' }}>
                        <StageBadge stage={getDisplayStage(lead)} junkReason={lead.junkReason} />
                      </div>
                      <div style={{ padding: '14px 12px', fontSize: 'var(--evo-list-subtitle-fs)', color: 'var(--evo-fg-3)', textAlign: 'right' }}>
                        {fmtDate(lead.updatedAt)}
                      </div>
                    </>
                  )}
                </div>
                <div style={{ margin: '0 var(--evo-list-pad-x)', height: '1px', background: 'var(--evo-stroke)' }} />
              </div>
            )}
            loadMoreSentinel={
              <div ref={sentinelRef} style={{ height: 1 }}>
                {isFetchingNextPage && <LoadingState size="sm" />}
              </div>
            }
            emptyState={<EmptyState size="md" reserveSpace title={t('leads.noResults')} />}
          />
        )}
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function LeadsPage() {
  const { t } = useTranslation();
  const { setOverride } = useTopBarCtx();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState<'list' | 'pipeline'>('list');
  const [filters, setFilters] = useState<LeadFilters>(() => filtersFromParams(searchParams));
  const [search, setSearch] = useState(searchParams.get('search') ?? '');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [showScrape, setShowScrape] = useState(false);
  const [showNewLead, setShowNewLead] = useState(false);

  const bulkUpdate = useBulkUpdateStage();
  const bulkDelete = useBulkDeleteLeads();
  const bulkAnalyze = useBulkAnalyze();
  const bulkDraft = useBulkDraft();
  const bulkSend = useBulkSend();
  const bulkJunk = useBulkJunk();
  const bulkArchive = useBulkArchive();
  const bulkReactivate = useBulkReactivate();
  const bulkConvert = useBulkConvertLeads();
  const toast = useToast();
  const { data: dueData } = useDueDraftsCount();
  const dueCount = dueData?.count ?? 0;
  const { data: idsData } = useFilteredLeadIds({
    ...(filters.search ? { search: filters.search } : {}),
    ...(filters.pipelineStage ? { pipelineStage: filters.pipelineStage } : {}),
    ...(filters.city ? { city: filters.city } : {}),
    ...(filters.hasWebsite !== undefined ? { hasWebsite: filters.hasWebsite } : {}),
    ...(filters.hasEmail !== undefined ? { hasEmail: filters.hasEmail } : {}),
    ...(filters.hasDueDrafts ? { hasDueDrafts: true as const } : {}),
  });
  const filteredIds = idsData?.ids ?? [];

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.pipelineStage) params.set('pipelineStage', filters.pipelineStage);
    if (filters.city) params.set('city', filters.city);
    if (filters.hasWebsite !== undefined) params.set('hasWebsite', String(filters.hasWebsite));
    if (filters.hasEmail !== undefined) params.set('hasEmail', String(filters.hasEmail));
    if (filters.hasDueDrafts) params.set('hasDueDrafts', 'true');
    if (filters.page && filters.page > 1) params.set('page', String(filters.page));
    if (filters.sortBy) params.set('sortBy', filters.sortBy);
    if (filters.sortDir) params.set('sortDir', filters.sortDir);
    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  useEffect(() => {
    setOverride({
      title: t('leads.pageTitle'),
      heroIcon: 'bolt-large',
      heroIconColor: 'var(--evo-steel)',
      bottomActions: (
        <PillGroup size="md">
          <Pill tone="cyan" active={view === 'list'} onClick={() => setView('list')}>
            {t('leads.viewList')}
          </Pill>
          <Pill tone="blue" active={view === 'pipeline'} onClick={() => setView('pipeline')}>
            {t('leads.viewPipeline')}
          </Pill>
          <Pill tone="warn" icon="leads" onClick={() => setShowScrape(true)}>
            {t('leads.scrapeBtn')}
          </Pill>
          <Pill tone="cyan" onClick={() => setShowNewLead(true)}>
            {t('leads.add.newLeadBtn')}
          </Pill>
        </PillGroup>
      ),
    });
    return () => setOverride({});
  }, [setOverride, t, view]);

  function toggleSelect(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function handleBulkAnalyze() {
    const ids = Array.from(selected);
    bulkAnalyze.mutate(ids, {
      onSuccess: () => { setSelected(new Set()); toast.info(t('leads.bulkActions.started', { count: ids.length }), { description: t('leads.bulkActions.checkPill') }); },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkDraft() {
    const ids = Array.from(selected);
    bulkDraft.mutate(ids, {
      onSuccess: () => { setSelected(new Set()); toast.info(t('leads.bulkActions.started', { count: ids.length }), { description: t('leads.bulkActions.checkPill') }); },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkSend() {
    const ids = Array.from(selected);
    bulkSend.mutate(ids, {
      onSuccess: () => { setSelected(new Set()); toast.info(t('leads.bulkActions.started', { count: ids.length }), { description: t('leads.bulkActions.checkPill') }); },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkJunk() {
    const ids = Array.from(selected);
    bulkJunk.mutate({ ids }, {
      onSuccess: (res) => { setSelected(new Set()); toast.success(t('leads.bulkActions.junkSuccess', { count: res.updated })); },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkArchive() {
    const ids = Array.from(selected);
    bulkArchive.mutate(ids, {
      onSuccess: (res) => { setSelected(new Set()); toast.success(t('leads.bulkActions.archiveSuccess', { count: res.updated })); },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkReactivate() {
    const ids = Array.from(selected);
    bulkReactivate.mutate(ids, {
      onSuccess: (res) => { setSelected(new Set()); toast.success(t('leads.bulkActions.reactivateSuccess', { count: res.updated })); },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  function handleBulkConvert() {
    const ids = Array.from(selected);
    bulkConvert.mutate(ids, {
      onSuccess: (res) => {
        setSelected(new Set());
        toast.success(t('leads.bulkActions.convertDone', {
          created: res.created,
          alreadyLinked: res.alreadyLinked,
          noEmail: res.noEmail,
          matchFound: res.matchFound,
        }));
      },
      onError: () => toast.error(t('leads.bulkActions.failed')),
    });
  }

  // True when the current view shows terminal-status leads. Backend now unifies
  // archived + junk + disqualified behind pipelineStage='archived', so this
  // single check covers all three. In this mode we surface a Reactivate bulk
  // action and swap the Updated column for a Reason column.
  const isTerminalView = filters.pipelineStage === 'archived';

  const quickFilter = filters.hasWebsite === true ? 'with_website'
    : filters.hasWebsite === false ? 'without_website'
    : filters.hasEmail === true ? 'with_email'
    : filters.hasEmail === false ? 'without_email'
    : '';

  function applySearch() {
    setFilters(f => ({ ...f, page: 1, ...(search ? { search } : {}) }));
  }

  function clearSearch() {
    setSearch('');
    setFilters(f => {
      const { search: _s, ...rest } = f;
      return { ...rest, page: 1 };
    });
  }

  return (
    <div className="flex flex-col h-full animate-fade-blur-in">
      {/* Filters bar (list view only) */}
      {view === 'list' && (
        <FilterRow>
          <FilterRow.Search
            value={search}
            onChange={setSearch}
            onSubmit={applySearch}
            onClear={clearSearch}
            placeholder={t('leads.searchPlaceholder')}
          />

          {dueCount > 0 && (
            <Chip
              tone="info"
              label={t('leads.dueNowFilter', { count: dueCount })}
              selected={!!filters.hasDueDrafts}
              onClick={() => setFilters(f => f.hasDueDrafts ? { page: 1, limit: f.limit ?? 50 } : { ...f, page: 1, hasDueDrafts: true })}
            />
          )}

          <FilterRow.Dropdown<string>
            value={filters.pipelineStage ?? ''}
            placeholder={t('leads.allStages')}
            options={[
              { value: '', label: t('leads.allStages') },
              ...STAGES.map(s => ({ value: s.id, label: t('leads.stages.' + s.id) })),
              { value: 'archived', label: t('leads.stages.archived') },
            ]}
            onChange={val => setFilters(f => { const { pipelineStage: _ps, ...rest } = f; return val ? { ...rest, page: 1, pipelineStage: val } : { ...rest, page: 1 }; })}
          />

          <FilterRow.Dropdown<string>
            value={quickFilter}
            placeholder={t('leads.filters.all')}
            options={[
              { value: '', label: t('leads.filters.all') },
              { value: 'with_website', label: t('leads.filters.withWebsite') },
              { value: 'without_website', label: t('leads.filters.withoutWebsite') },
              { value: 'with_email', label: t('leads.filters.withEmail') },
              { value: 'without_email', label: t('leads.filters.withoutEmail') },
            ]}
            onChange={val => {
              setFilters(f => {
                const { hasWebsite: _hw, hasEmail: _he, ...rest } = f;
                if (!val) return { ...rest, page: 1 };
                if (val === 'with_website') return { ...rest, page: 1, hasWebsite: true };
                if (val === 'without_website') return { ...rest, page: 1, hasWebsite: false };
                if (val === 'with_email') return { ...rest, page: 1, hasEmail: true };
                if (val === 'without_email') return { ...rest, page: 1, hasEmail: false };
                return { ...rest, page: 1 };
              });
            }}
          />
        </FilterRow>
      )}

      <SelectionActionBar
        visible={selected.size > 0}
        countLabel={t('leads.selected', { count: selected.size })}
        onClearSelection={() => setSelected(new Set())}
      >
        {isTerminalView ? (
          // Terminal-status view (archived/junk/unqualified): the only sensible
          // bulk action is Reactivate — putting leads back into status='enriched'
          // /pipeline_stage='new' so the normal pipeline can resume on them.
          <>
            <SelectionActionBar.Action
              label={t('leads.bulkActions.reactivate')}
              tone="primary"
              loading={bulkReactivate.isPending}
              onClick={handleBulkReactivate}
              confirmRequired
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.Action
              label={t('common.delete')}
              tone="danger"
              loading={bulkDelete.isPending}
              onClick={() => bulkDelete.mutate(Array.from(selected), { onSuccess: () => setSelected(new Set()) })}
              confirmRequired
            />
          </>
        ) : (
          <>
            <Dropdown<string>
              value={null}
              placeholder={t('leads.moveToStage')}
              options={[
                { value: 'analyzed', label: t('leads.stages.analyzed') },
                { value: 'closed_won', label: t('leads.stages.closed_won') },
                { value: 'closed_lost', label: t('leads.stages.closed_lost') },
              ]}
              onChange={val => {
                if (!val || selected.size === 0) return;
                bulkUpdate.mutate(
                  { ids: Array.from(selected), stage: val },
                  { onSuccess: () => setSelected(new Set()) },
                );
              }}
              size="sm"
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.Action
              label={t('leads.bulkActions.analyze')}
              loading={bulkAnalyze.isPending}
              onClick={handleBulkAnalyze}
              confirmRequired
              disabled={!!filters.pipelineStage && !['new', 'enriched', 'analyzed'].includes(filters.pipelineStage)}
            />
            <SelectionActionBar.Action
              label={t('leads.bulkActions.draft')}
              loading={bulkDraft.isPending}
              onClick={handleBulkDraft}
              confirmRequired
              disabled={!!filters.pipelineStage && filters.pipelineStage !== 'analyzed'}
            />
            <SelectionActionBar.Action
              label={t('leads.bulkActions.send')}
              tone="primary"
              loading={bulkSend.isPending}
              onClick={handleBulkSend}
              confirmRequired
              disabled={!!filters.pipelineStage && filters.pipelineStage !== 'drafted'}
            />
            <SelectionActionBar.Action
              label={t('leads.bulkActions.convertToContact')}
              tone="primary"
              loading={bulkConvert.isPending}
              onClick={handleBulkConvert}
              confirmRequired
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.Action
              label={t('leads.bulkActions.archive')}
              tone="danger"
              loading={bulkArchive.isPending}
              onClick={handleBulkArchive}
              confirmRequired
            />
            <SelectionActionBar.Action
              label={t('leads.bulkActions.junk')}
              tone="danger"
              loading={bulkJunk.isPending}
              onClick={handleBulkJunk}
              confirmRequired
            />
            <SelectionActionBar.Divider />
            <SelectionActionBar.Action
              label={t('common.delete')}
              tone="danger"
              loading={bulkDelete.isPending}
              onClick={() => bulkDelete.mutate(Array.from(selected), { onSuccess: () => setSelected(new Set()) })}
              confirmRequired
            />
          </>
        )}
      </SelectionActionBar>

      {/* Main content area */}
      <div className="flex-1 overflow-hidden">
        {view === 'pipeline' ? (
          <PipelineBoard onSelect={lead => navigate('/leads/' + lead.id)} />
        ) : (
          <LeadsList
            filters={filters}
            selected={selected}
            onSelect={lead => navigate('/leads/' + lead.id, { state: { filteredIds, filters } })}
            onToggleSelect={toggleSelect}
            onFiltersChange={setFilters}
          />
        )}
      </div>

      {/* Run pipeline modal */}
      {showScrape && <RunPipelineModal onClose={() => setShowScrape(false)} />}

      {/* New lead modal */}
      {showNewLead && (
        <NewLeadModal
          onClose={() => setShowNewLead(false)}
          onCreated={id => navigate('/leads/' + id)}
        />
      )}
    </div>
  );
}
